/**
 * Value types for the PWA app-icon badge counter.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BadgeCountValue.swift` — iOS is treated
 * as canonical; any future divergence should be resolved by updating this file
 * to match iOS, not the other way around.
 *
 * THREAT MODEL
 *   The badge counter is the single observable side channel that the OS
 *   exposes "outside" the encrypted store: anyone glancing at the home screen
 *   (or PWA icon on Android) learns "this user has N pending messages." We
 *   accept that leak — the count is what the badge feature exists to show —
 *   but we lock the surrounding behaviour:
 *
 *   - The badge total NEVER comes from the server. The number is computed
 *     locally from a counter that lives in IndexedDB (single record, single
 *     origin, shared across the main thread and the Service Worker via the
 *     same IDB origin). A hostile server cannot inflate the user's badge
 *     count to extort attention or signal a co-located observer.
 *   - The counter is monotonic at the increment site (>= 0) and clamps at
 *     zero on decrement. There is no negative-count "the user has read more
 *     than they received" state to leak.
 *   - Per-thread mute is honoured by `BadgeCountPolicy`, NOT by
 *     `BadgeCountStore` — the store has no thread context. A muted thread
 *     that triggers a push notification still pulls bytes through the wire
 *     (the deniable-block style invariant), but the policy decides not to
 *     bump the badge so the OS count does not change.
 *   - `navigator.setAppBadge()` is intentionally NOT called from this module.
 *     The consumer (Service Worker push handler or main-thread coordinator)
 *     reads `BadgeCountStore.current()` and drives the OS badge itself. This
 *     keeps the store Service-Worker-importable with zero DOM dependencies.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs.
 *   Allowed: IndexedDB, SubtleCrypto, standard JS globals.
 */

/** Practical upper bound so a runaway increment cannot blow up the IDB record.
 *  Past 99 the browser renders "99+" anyway; clamping at 99_999 keeps
 *  internal accounting bounded exactly as iOS does.
 */
export const MAX_BADGE_COUNT = 99_999;

/**
 * Snapshot of the badge counter at a point in time. The counter is always
 * non-negative — `BadgeCountStore` clamps at zero on decrement and refuses
 * negative `rebase` targets.
 */
export interface BadgeCountSnapshot {
  /** Current unread count. Always >= 0. Capped at MAX_BADGE_COUNT. */
  readonly total: number;
  /** Unix-millisecond timestamp of the last mutation. -Infinity for fresh store. */
  readonly lastUpdatedAt: number;
}

/** Sentinel `lastUpdatedAt` for a store that has never been written.
 *  Mirrors iOS `Date.distantPast`. */
export const DISTANT_PAST = -Infinity;

/** Errors emitted by the badge-count subsystem. Mirrors iOS `BadgeCountError`. */
export type BadgeCountError =
  | { readonly kind: "negativeCount"; readonly claimed: number }
  | { readonly kind: "clockSkew"; readonly claimed: number; readonly observed: number };

/**
 * Clamp `n` into [0, MAX_BADGE_COUNT].
 * The clamp is the only place the bounds are enforced, so changing MAX_BADGE_COUNT
 * automatically propagates to increment/decrement/rebase.
 */
export function clampBadgeCount(n: number): number {
  return Math.min(Math.max(0, n), MAX_BADGE_COUNT);
}
